import "common/form/deprecated_form.scss";

import { useState, useEffect, useRef } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "common/core/button";
import DocumentUploaderManager from "common/document/uploader/manager";
import { deprecatedInteractingWithInput } from "common/form/util";
import Env from "config/environment";
import { onlyRequiresEsign } from "util/completion_requirements/completion_requirements_text";
import { ROUTE as TEMPLATES_ROUTE } from "common/tools/document_templates";
import {
  TemplateTagsEnum,
  OrganizationDocumentTemplateState,
  ProcessingStates,
} from "graphql_globals";
import type { useMutation } from "util/graphql";
import { EasylinkPreviewSection } from "common/easylink_template_preview";
import { useMoveTools } from "util/feature_detection";
import {
  onReplaceDocument,
  type FileReplace,
} from "common/document/uploader/replace_document_uploader";
import { useForm } from "common/core/form";
import { NOTIFICATION_SUBTYPES } from "constants/notifications";
import { StyledTextInput } from "common/core/form/text";
import { captureException } from "util/exception";
import { pushNotification } from "common/core/notification_center/actions";
import { FormattedFieldError } from "common/core/form/error";
import {
  Checkbox,
  CheckboxGroup,
  CheckboxLabel,
  RadioGroup,
  RadioInput,
  RadioLabel,
} from "common/core/form/option";
import { isGraphQLError } from "util/graphql/query";
import { PERMALINK_FORMAT } from "constants/validation";

import type {
  TemplateRoot_template_OrganizationDocumentTemplate as Template,
  TemplateRoot_organization_Organization as Organization,
} from "../template_query.graphql";
import type {
  UpdateOrganizationDocumentTemplate_updateOrganizationDocumentTemplate_organizationDocumentTemplate as OrganizationDocumentTemplate,
  UpdateOrganizationDocumentTemplate_updateOrganizationDocumentTemplate_organizationDocumentTemplate_documentBundle_documents_edges as DocumentEdges,
  UpdateOrganizationDocumentTemplate_updateOrganizationDocumentTemplate_organizationDocumentTemplate_documentBundle_documents_edges_document as Document,
  UpdateOrganizationDocumentTemplateVariables,
  UpdateOrganizationDocumentTemplate as UpdateOrganizationDocumentTemplateMutation,
  UpdateOrganizationDocumentTemplate_updateOrganizationDocumentTemplate_errors as Errors,
} from "../update_organization_document_template_mutation.graphql";
import type {
  DeleteSelectedOrganizationDocumentTemplates as DeleteSelectedOrganizationDocumentTemplatesMutation,
  DeleteSelectedOrganizationDocumentTemplatesVariables,
} from "../delete_selected_organization_document_templates_mutation.graphql";
import { documentsInput } from "../util";
import ProcessingFailedModal from "./processing_failed_modal";
import TemplateDocumentUploader from "./uploader";

type EncompassTagValue = TemplateTagsEnum;

const TEMPLATE_TAG_OPTIONS: { label: TemplateTagsEnum; value: EncompassTagValue }[] = Object.values(
  TemplateTagsEnum,
).map((tag) => ({
  label: tag,
  value: tag,
}));

const { industryTemplatesOrgId } = Env;

const MESSAGES = defineMessages({
  templateName: {
    id: "c4c86161-a4ed-4b3d-b6e9-239bee9bfc7e",
    defaultMessage: "Template name",
  },
  displayName: {
    id: "10b1e563-d751-4ea3-8a61-8c55b695ba06",
    defaultMessage: "Display name (optional)",
  },
  permalink: {
    id: "9744be99-5c6a-4100-8858-37ad206853f6",
    defaultMessage: "Permalink for API use (optional)",
  },
  templateTags: {
    id: "dd7718dc-2990-4b75-a2df-fe8b1723883b",
    defaultMessage: "Template tags",
  },
  permalinkError: {
    id: "024909d7-9c1e-4272-a7de-94ca01cf1aab",
    defaultMessage: "Permalink is already in use",
  },
  templateError: {
    id: "34494340-7efe-4f6a-9c67-d551ecbd62f8",
    defaultMessage: "Error saving template",
  },
});

export type ChangeDocumentProps = {
  signingRequiresMeeting: boolean;
  signerCanAnnotate: boolean;
  witnessRequired: boolean;
  notarizationRequired: boolean;
  proofingRequired: boolean;
};

type FormValues = {
  displayName: string | null;
  permalink: string | null;
  templateName: string;
  activationState: OrganizationDocumentTemplateState;
  encompassTags: TemplateTagsEnum[];
};

type Props = {
  organization: Organization;
  template: Template;
  isMortgage?: boolean;
  updateTemplateMutateFn: ReturnType<
    typeof useMutation<
      UpdateOrganizationDocumentTemplateMutation,
      UpdateOrganizationDocumentTemplateVariables
    >
  >;
  deleteTemplateMutateFn: ReturnType<
    typeof useMutation<
      DeleteSelectedOrganizationDocumentTemplatesMutation,
      DeleteSelectedOrganizationDocumentTemplatesVariables
    >
  >;
};

type UpdateTemplateProps = FormValues & {
  quiet?: boolean;
  nextPage?: string;
};

export function OrganizationTemplateEditForm(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [processingDocumentIds, setProcessingDocumentIds] = useState<string[]>([]);
  const [showProcessingFailedModal, setShowProcessingFailedModal] = useState(false);
  const [documentToReplace, setDocumentToReplace] = useState<Document | null>(null);

  const mounted = useRef(false);
  const moveTools = useMoveTools();
  const intl = useIntl();
  const {
    template: { name, displayName, permalink, activationState, encompassTags },
  } = props;

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      templateName: name,
      displayName: displayName || "",
      permalink: permalink || "",
      activationState,
      encompassTags,
    },
  });

  const encompassTagsWatch: TemplateTagsEnum[] = watch("encompassTags");

  useEffect(() => {
    mounted.current = true;
    const {
      template: { documentBundle },
    } = props;
    // Set initial state if any docs are still processing.  This is necessary
    // for new templates because the first doc will have just been uploaded
    // when this component mounts.
    const processingDocumentIds = documentBundle.documents.edges
      .filter((edge) => edge.document.processingState === ProcessingStates.PENDING)
      .map((edge) => edge.document.id);
    setProcessingDocumentIds(processingDocumentIds);

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      mounted.current = false;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function getBasePath() {
    const { pathname } = location;
    // BIZ-7111: Always use the new hardcoded tools path.
    return moveTools ? `tools/${TEMPLATES_ROUTE}` : pathname.split("/")[1];
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (deprecatedInteractingWithInput()) {
      return;
    }

    if (e.key === "Escape") {
      returnToList();
    }
  }

  const handleTagChange = (tagValue: TemplateTagsEnum, checked: boolean) => {
    setValue(
      "encompassTags",
      checked
        ? [...encompassTagsWatch, tagValue]
        : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          encompassTagsWatch.filter((t) => t !== tagValue), // eventually will have more than one tag value
    );
  };

  function updateTemplateTools(args: FormValues) {
    return updateTemplate({ ...args, nextPage: "tools" });
  }

  function updateTemplateExit(args: FormValues) {
    return updateTemplate({ ...args, nextPage: "exit" });
  }

  function updateTemplate(args: UpdateTemplateProps) {
    const {
      templateName,
      displayName,
      permalink,
      quiet,
      activationState,
      nextPage = "prepare",
      encompassTags,
    } = args;

    const { template } = props;
    const { search } = location;
    const basePath = getBasePath();
    const { id, documentBundle } = template;

    setIsSaving(true);

    return props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            name: templateName.trim(),
            displayName: displayName?.trim(),
            permalink: permalink ? lowerCasePermalink(permalink.trim()) : undefined,
            activationState,
            encompassTags,
          },
        },
        optimisticResponse: {
          updateOrganizationDocumentTemplate: {
            __typename: "UpdateOrganizationDocumentTemplateMutationPayload",
            errors: null,
            organizationDocumentTemplate: {
              ...template,
              name: templateName.trim(),
              displayName: displayName?.trim() || null,
              activationState,
              permalink: permalink ? lowerCasePermalink(permalink.trim()) : null,
              encompassTags,
            },
          },
        },
      })
      .then(() => {
        setIsSaving(false);
        if (quiet) {
          return;
        }
        if (nextPage === "tools") {
          navigate(`/${basePath}/${template.id}/tools/${search}`);
        } else if (nextPage === "exit") {
          const wasSearching = location.state;
          if (wasSearching) {
            returnToQuery();
          } else {
            returnToList();
          }
          // skip the annotation screen if no documents in the bundle
        } else if (!documentBundle.documents.edges.length) {
          navigate(`/${basePath}`);
        } else {
          navigate(`/${basePath}/${id}${search}`);
        }
      })
      .catch((error) => {
        setIsSaving(false);
        captureException(error);
        if (isGraphQLError(error) && error.graphQLErrors[0]?.message === "non_unique_permalink") {
          pushNotification({
            message: intl.formatMessage(MESSAGES.permalinkError),
            subtype: NOTIFICATION_SUBTYPES.ERROR,
          });
        } else {
          pushNotification({
            message: intl.formatMessage(MESSAGES.templateError),
            subtype: NOTIFICATION_SUBTYPES.ERROR,
          });
        }
      });
  }

  function updateDocuments(newDocumentEdges: DocumentEdges[]) {
    const { template } = props;
    const { id, name, displayName, permalink, documentBundle, activationState } = template;

    setIsSaving(true);

    return props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            documents: documentsInput(newDocumentEdges),
            permalink,
            activationState,
            name,
            displayName,
          },
        },
        optimisticResponse: {
          updateOrganizationDocumentTemplate: {
            __typename: "UpdateOrganizationDocumentTemplateMutationPayload",
            errors: null,
            organizationDocumentTemplate: {
              ...template,
              documentBundle: {
                ...documentBundle,
                documents: {
                  __typename: "DocumentConnection",
                  edges: newDocumentEdges,
                },
              },
            },
          },
        },
      })
      .finally(() => {
        setIsSaving(false);
      });
  }

  function returnToList() {
    const { search } = location;
    const basePath = getBasePath();
    navigate(`/${basePath}${search}`);
  }

  function returnToQuery() {
    const { search } = location;
    const basePath = getBasePath();
    navigate(`/${basePath}${search}`);
  }

  function lowerCasePermalink(value: string) {
    return value.toLowerCase();
  }

  function deleteTemplate() {
    const {
      template: { id },
      deleteTemplateMutateFn,
    } = props;

    setIsCancelling(true);

    deleteTemplateMutateFn({
      variables: { input: { ids: [id] } },
      optimisticResponse: {
        deleteSelectedOrganizationDocumentTemplates: {
          organizationTemplateIds: [id],
        },
      },
    })
      .then(() => {
        setIsCancelling(false);
        returnToList();
      })
      .catch(() => setIsCancelling(false));
  }

  function addDocument(
    obj: { s3Key: string; filename: string },
    onSuccess: (arg: OrganizationDocumentTemplate) => void,
    onFailure: (errors: Errors[]) => void,
  ) {
    const {
      template: {
        id,
        permalink,
        documentBundle: { documents },
        name,
        displayName,
        activationState,
      },
    } = props;

    setIsSaving(true);

    const originalDocuments = documentsInput(documents.edges);
    const newDocument = {
      fileHandle: obj.s3Key,
      name: obj.filename,
      customerCanAnnotate: true,
      witnessRequired: false,
      notarizationRequired: true,
      proofingRequired: false,
      esign: false,
    };
    const newDocuments = [...originalDocuments, newDocument];

    return props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            documents: newDocuments,
            permalink,
            name,
            displayName,
            activationState,
          },
        },
      })
      .then((response) => {
        setIsSaving(false);
        const { organizationDocumentTemplate, errors } =
          response.data?.updateOrganizationDocumentTemplate || {};
        if (errors) {
          onFailure(errors);
        } else {
          onSuccess(organizationDocumentTemplate!);
        }
      });
  }

  function handleDocumentProcessingStarted(documentId: string) {
    if (!mounted.current) {
      return; // callback is not needed if unmounted
    }
    setProcessingDocumentIds((prev) => prev.concat(documentId));
  }

  function handleDocumentProcessingDone(documentId: string) {
    if (!mounted.current) {
      return; // callback is not needed if unmounted
    }
    setProcessingDocumentIds((prev) => prev.filter((id) => id !== documentId));
  }

  function handleDocumentProcessingFailed(documentId: string) {
    if (!mounted.current) {
      return; // callback is not needed if unmounted
    }

    // Backend does not delete the doc from the bundle automatically, so we do it here
    const {
      template: {
        documentBundle: { documents },
      },
    } = props;
    const docEdge = documents.edges.find((edge) => edge.document.id === documentId);
    if (docEdge) {
      handleDeleteDocument(docEdge.document);
    }

    setProcessingDocumentIds((prev) => prev.filter((id) => id !== documentId));
    setShowProcessingFailedModal(true); // Inform the user that the doc is not processable
  }

  function closeProcessingFailedModal() {
    setShowProcessingFailedModal(false);
  }

  function handleChangeDocumentProperties(
    propValues: ChangeDocumentProps,
    document: Document | null,
  ) {
    const {
      template: {
        documentBundle: { documents },
      },
    } = props;
    if (document) {
      const documentId = document.id;
      const newDocumentEdges = documents.edges.map((edge) => {
        return edge.document.id === documentId
          ? { ...edge, document: { ...edge.document, ...propValues } }
          : edge;
      });
      updateDocuments(newDocumentEdges);
    }
  }

  function handleReplaceDocument(newDocument: FileReplace) {
    const {
      template: {
        documentBundle: { documents },
      },
    } = props;

    const replaceDocumentFn = async () => {
      if (!documentToReplace) {
        return null;
      }

      const newDocumentEdges = documents.edges.map((edge) => {
        return edge.document.id === documentToReplace.id
          ? { ...edge, document: { ...edge.document, documentToCopyId: newDocument.id } }
          : edge;
      });

      const response = await updateDocuments(newDocumentEdges);

      const { errors } = response.data?.updateOrganizationDocumentTemplate || {};

      return errors;
    };

    return onReplaceDocument<Errors>(replaceDocumentFn, newDocument.name);
  }

  function handleDeleteDocument(document: Document) {
    const {
      template: {
        documentBundle: { documents },
      },
    } = props;
    const documentId = document.id;
    const newDocumentEdges = documents.edges.filter((edge) => {
      return edge.document.id !== documentId;
    });
    updateDocuments(newDocumentEdges);
  }

  function handleRenameDocument(document: Document, newName: string) {
    const {
      template: {
        documentBundle: { documents },
      },
    } = props;
    const documentId = document.id;
    const newDocumentEdges = documents.edges.map((edge) => {
      return edge.document.id === documentId
        ? { ...edge, document: { ...edge.document, name: newName } }
        : edge;
    });
    updateDocuments(newDocumentEdges);
  }

  const { organization, template, isMortgage } = props;
  const documentBundle = template.documentBundle;
  const documents = documentBundle.documents.edges.map((edge) => edge.document);
  const isNewTemplate = location.pathname.endsWith("/new");

  const isProcessing = processingDocumentIds.length > 0;
  const isEsign = onlyRequiresEsign(template.documentBundle.completionRequirements);
  const templateStates = [
    OrganizationDocumentTemplateState.ACTIVE,
    OrganizationDocumentTemplateState.DRAFT,
    OrganizationDocumentTemplateState.DISABLED,
  ];

  const cancelButtonClicked = () => {
    const wasSearching = location.state;
    if (isNewTemplate) {
      deleteTemplate();
    } else if (wasSearching) {
      returnToQuery();
    } else {
      returnToList();
    }
  };

  return (
    <form className="OrganizationTemplateEdit--Form">
      {showProcessingFailedModal && <ProcessingFailedModal onClose={closeProcessingFailedModal} />}

      {template.preset && (
        <Button
          onClick={handleSubmit(updateTemplateTools)}
          variant="secondary"
          buttonColor="action"
          className="OrganizationTemplateEdit--Form--Button"
          automationId="templates-developer-tools"
        >
          <FormattedMessage
            id="277633fa-c45c-41b9-9c67-49109be0efd8"
            defaultMessage="Template settings"
          />
        </Button>
      )}

      {(template.preset || organization.id === industryTemplatesOrgId) && (
        <div className="OrganizationTemplateTools--Header--Toggle">
          <RadioGroup horizontal aria-labelledby="template-state-radio-group">
            {templateStates.map((state) => (
              <RadioLabel
                key={state}
                label={state}
                radio={
                  <RadioInput
                    value={state}
                    {...register("activationState")}
                    data-automation-id={state}
                  />
                }
              />
            ))}
          </RadioGroup>
        </div>
      )}
      <div className="OrganizationTemplateEdit--Form--Group">
        <StyledTextInput
          placeholder="e.g. Power of Attorney for notarization"
          label={intl.formatMessage(MESSAGES.templateName)}
          data-automation-id="template-name-field"
          displayRequiredAsterisk
          aria-invalid={!!errors.templateName}
          required
          {...register("templateName", {
            required: true,
          })}
        />
        {errors.templateName && (
          <FormattedFieldError inputName="templateName" error={errors.templateName} />
        )}
      </div>
      {template.preset && (
        <div className="OrganizationTemplateEdit--Form--Group">
          <StyledTextInput
            label={intl.formatMessage(MESSAGES.displayName)}
            displayRequiredAsterisk
            data-automation-id="template-display-name-field"
            aria-invalid={!!errors.displayName}
            {...register("displayName")}
          />
          {errors.displayName && (
            <FormattedFieldError inputName="displayName" error={errors.displayName} />
          )}
        </div>
      )}

      {!template.preset && (
        <div className="OrganizationTemplateEdit--Form--Group">
          <StyledTextInput
            id="permalink"
            label={intl.formatMessage(MESSAGES.permalink)}
            data-automation-id="permalink-field"
            aria-invalid={!!errors.permalink}
            {...register("permalink", {
              pattern: PERMALINK_FORMAT,
            })}
          />
          {errors.permalink && (
            <FormattedFieldError inputName="permalink" error={errors.permalink} />
          )}
        </div>
      )}

      {template.preset && (
        <>
          <div className="OrganizationTemplateEdit--TemplateTags">
            <CheckboxGroup label={intl.formatMessage(MESSAGES.templateTags)}>
              {TEMPLATE_TAG_OPTIONS.map((tag) => (
                <CheckboxLabel
                  key={tag.value}
                  label={tag.label}
                  checkbox={
                    <Checkbox
                      data-automation-id="save-template-tags"
                      checked={encompassTagsWatch.includes(tag.value)}
                      aria-invalid="false"
                      onChange={(e) => {
                        handleTagChange(tag.value, e.target.checked);
                      }}
                    />
                  }
                />
              ))}
            </CheckboxGroup>
          </div>
        </>
      )}

      <div className="OrganizationTemplateEdit--Uploader">
        <DocumentUploaderManager
          documents={documents}
          documentBundleId={documentBundle.id}
          onChangeDocumentProperties={handleChangeDocumentProperties}
          onDeleteDocuments={handleDeleteDocument}
          setDocumentToReplace={setDocumentToReplace}
          onReplaceDocument={handleReplaceDocument}
          onRenameDocument={handleRenameDocument}
          showWitnessRequired={!isEsign}
          onDocumentProcessingStarted={handleDocumentProcessingStarted}
          onDocumentProcessingDone={handleDocumentProcessingDone}
          onDocumentProcessingFailed={handleDocumentProcessingFailed}
          disableClick
          hideAnnotateButton
          organization={organization}
          isMortgage={isMortgage}
          isTemplateEditing
          templateId={template.id}
        />
      </div>

      <div className="OrganizationTemplateEdit--Uploader--Button--Container">
        <TemplateDocumentUploader
          title={
            <FormattedMessage
              id="9744be99-5c6a-4100-8858-37ad206853f6"
              defaultMessage="Add document"
            />
          }
          buttonClassName="OrganizationTemplateEdit--Uploader--Button"
          buttonStyleProps={{
            withIcon: { name: "plus", placement: "left" },
            children: "Add document",
          }}
          onChangeDocument={addDocument}
        />
      </div>

      {template.preset && (
        <div className="OrganizationTemplateInformationalText">
          {
            <span>
              To place signer fields on your document template, choose “Prepare document.”
              Otherwise, "Save and exit."
            </span>
          }
        </div>
      )}

      {template.easylinks.length > 0 && <EasylinkPreviewSection easylinks={template.easylinks} />}

      <div className="OrganizationTemplateEdit--options">
        <Button
          automationId="cancel-button"
          isLoading={isCancelling}
          disabled={isCancelling}
          onClick={cancelButtonClicked}
          variant="tertiary"
          buttonColor="action"
        >
          <FormattedMessage id="75b26d71-b425-4205-9d16-56bb830de3b5" defaultMessage="Cancel" />
        </Button>

        <Button
          onClick={handleSubmit(updateTemplateExit)}
          isLoading={isSaving || isCancelling || isSubmitting}
          disabled={isSaving || isCancelling || isSubmitting}
          automationId="save-exit-button"
          className="OrganizationTemplateEdit--Uploader--SaveExitButton"
          variant="secondary"
          buttonColor="action"
        >
          <FormattedMessage
            id="a5823a1b-7f91-48e0-8d3c-c49d2200d8e5"
            defaultMessage="Save and exit"
          />
        </Button>

        <Button
          onClick={handleSubmit(updateTemplate)}
          isLoading={isSaving || isProcessing || isSubmitting}
          disabled={isSaving || isProcessing || isSubmitting}
          automationId="save-template-button"
          className="OrganizationTemplateEdit--Uploader--NextButton"
          variant="primary"
          buttonColor="action"
        >
          <FormattedMessage
            id="a4823a1b-7f91-48e0-8d3c-c49d2200d8e5"
            defaultMessage="Prepare document"
          />
        </Button>
      </div>
    </form>
  );
}
